import { Note, Sheet, SheetTabs, sheetTabsSheetStyle } from "@appretio/ui-kit";
import { type ReactNode, useEffect, useState } from "react";
import {
  MANAGE_CUBES_OPERATIONS,
  type ManageCubesOperation,
  OperationContent
} from "../components/manageCubesOperations/OperationContent";
import {
  manageCubesEmptyState,
  manageCubesSelectorRow
} from "../styles/pageLayout.css";

/** Props for the shared manage-cubes operation page shell. */
export interface ManageCubesOperationsPageProps {
  /** Selector controls rendered in the top sheet. */
  selector: ReactNode;
  /** Currently selected target cube id. */
  selectedCubeId: string | undefined;
  /** Empty-state text shown before a cube and operation are selected. */
  emptyStateText: string;
  /** Value that clears the selected operation when it changes. */
  resetKey?: string;
}

/** Shared selector-sheet and operation-tabs shell for manage-cubes pages. */
export function ManageCubesOperationsPage({
  selector,
  selectedCubeId,
  emptyStateText,
  resetKey
}: ManageCubesOperationsPageProps) {
  const [selectedOperation, setSelectedOperation] = useState<
    ManageCubesOperation | undefined
  >();

  useEffect(() => {
    setSelectedOperation(undefined);
  }, [resetKey, selectedCubeId]);

  return (
    <>
      <Sheet>
        <div className={manageCubesSelectorRow}>{selector}</div>
      </Sheet>
      <Sheet className={sheetTabsSheetStyle}>
        <SheetTabs
          value={selectedOperation ?? ""}
          onValueChange={(value) =>
            setSelectedOperation(value as ManageCubesOperation)
          }
        >
          <SheetTabs.Header>
            <SheetTabs.List>
              {MANAGE_CUBES_OPERATIONS.map((operation) => (
                <SheetTabs.Trigger
                  key={operation}
                  value={operation}
                  disabled={!selectedCubeId}
                >
                  {operation}
                </SheetTabs.Trigger>
              ))}
            </SheetTabs.List>
          </SheetTabs.Header>
          <SheetTabs.ContentWrapper
            activeValue={selectedOperation}
            firstValue={MANAGE_CUBES_OPERATIONS[0]}
          >
            {selectedOperation && selectedCubeId ? (
              <SheetTabs.Content value={selectedOperation}>
                <OperationContent
                  operation={selectedOperation}
                  cubeId={selectedCubeId}
                />
              </SheetTabs.Content>
            ) : (
              <div className={manageCubesEmptyState}>
                <Note icon="info" text={emptyStateText} />
              </div>
            )}
          </SheetTabs.ContentWrapper>
        </SheetTabs>
      </Sheet>
    </>
  );
}
